import React, {useEffect, useState} from 'react';
import {Line} from 'react-chartjs-2';
import moment from 'jalali-moment'
import {Chart} from 'chart.js/auto';
import {changePercentage, convertNumber} from '../../util/DominantUtil';
import ChartLoaderSummary from "../loaders/ChartLoaderSummary";
import axios from "../plugins/axios";

const DATE_FORMAT = 'D MMMM YYYY';
const RIAL_CURRENCY_SUFFIX = ' ریال';
const DOLLAR_CURRENCY_SUFFIX = ' دلار'

export default function HistoryChart({symbol}) {
    const [timeData, setTimeData] = useState(null)
    const [closeData, setCloseData] = useState(null)
    const [volumeData, setVolumeData] = useState(null)
    const [volumeMax, setVolumeMax] = useState(null);
    const [volumeColor, setVolumeColor] = useState(null);
    const [activeButtons, setActiveButtons] = useState([false, false, false, true, false, false])
    const [profit, setProfit] = useState(null);

    useEffect(() => {
        setTimeData(null)
        setActiveButtons([false, false, false, true, false, false])
        if (symbol) callApi("1w", "1y")
    }, [symbol])

    const data = {
        labels: timeData,
        datasets: [
            {
                type: 'bar',
                label: 'Volume',
                data: volumeData,
                backgroundColor: volumeColor,
                borderColor: '#fff',
                pointBorderWidth: 0,
                scale: 'vol',
                yAxisID: 'y1'
            },
            {
                type: 'line',
                label: 'Close',
                tension: 0,
                backgroundColor: '#0081F2',
                data: closeData,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointHitRadius: 20,
                yAxisID: 'y',
                fill: true
            },
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        stacked: false,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            threshold: false,
            legend: {
                display: false
            },
            tooltip: {
                rtl: true,
                titleAlign: 'center',
                titleFontFamily: '"IRANSans", IRANSans',
                bodyAlign: 'right',
                textDirection: 'rtl',
                intersect: false,
                animationDuration: 0,
                mode: 'index',
                position: 'nearest',
                callbacks: {
                    title: function (tooltipItem) {
                        const timestamp = timeData[tooltipItem[0].dataIndex]
                        return moment(new Date(timestamp)).locale('fa').format(DATE_FORMAT)
                    },
                    label: function (tooltipItem) {
                        const index = tooltipItem.datasetIndex;
                        if (index === 1) {
                            return "قیمت " + parseFloat(tooltipItem.raw)
                        }
                        const value = volumeData[tooltipItem.dataIndex];
                        return "حجم " + convertNumber(value, 2)
                    },
                },
            },
        },
        scales: {
            y: {
                display: false,
//                suggestedMax: volumeMax * 0.00000001,
            },
            y1: {
                display: false,
            },
            x: {
                display: true,
                beginAtZero: true,
                ticks: {
                    min: 0,
                    minRotation: 0,
                    maxRotation: 0,
                    maxTicksLimit: 4,
                    callback: function (value, index) {
                        if (timeData.length < 7 || index === 0 || index === timeData.length - 1) return ''
                        let format = timeData.length > 30 ? 'MMMM YYYY' : 'MMMM';
                        return moment(new Date(value)).locale('fa').format(format)
                    },
                    fontFamily: '"IRANSans", IRANSans',
                },
            }
        }
    }

    function callApi(interval = "1w", range = "1y") {
        axios
            .get(`https://api.bahabin.com/query/history/charts/v1/${symbol}?interval=${interval}&range=${range}`).then((response) => {
            const d = response.data.result;
            let open = d.open;
            let close = d.close;
            let volume = d.volume;

            setTimeData(d.time)
            setCloseData(close)
            setVolumeData(volume)
            setProfit(changePercentage(close[0], close[close.length - 1]))
            setVolumeMax(Math.max(...volume))
            const vColor = getVColor(open, close)
            setVolumeColor(vColor)
        }).catch((error) => {
            console.log(error)
        });
    }

    function getVColor(open, close) {
        return open.map((k, v) => close[v] < k ? '#EB0F29' : '#00873C');
    }

    function changeButtonClass(index) {
        const ab = activeButtons.map((v, k) => k === index)
        setActiveButtons(ab)
    }

    function getSpan(index, interval, range, text) {
        return <span className={`col history-date-range-btn ${activeButtons[index] ? 'history-active-button' : ''}`}
                     onClick={() => {
                         changeButtonClass(index)
                         return callApi(interval, range)
                     }}>{text}</span>;
    }

    return (
        <div className="text-right">
            <h2 className="text-right font-weight-bold">تاریخچه قیمت</h2>
            <div className="text-right mb-10px">
                        <span style={{fontSize: '13px'}}>میانگین بازدهی:
                            <span className="english-font d-inline-block history-chart-profit px-1">{profit}</span>
                        </span>
            </div>
            <div>
                <div className="chart-top-menu">
                    <div className="row no-gutters range-selectors text-center" dir="rtl">
                        {getSpan(0, '1d', '5d', '5 روز')}
                        {getSpan(1, '1d', '1mo', '1 ماه')}
                        {getSpan(2, '1d', '6mo', '6 ماه')}
                        {getSpan(3, '1w', '1y', '1 سال')}
                        {getSpan(4, '3mo', '5y', '5 سال')}
                        {getSpan(5, '3mo', 'max', 'همه')}
                    </div>
                </div>
                <div className="just-ltr live-chart-height cursor-pointer" style={{height: '215px'}}>
                    <Line
                        height={215}
                        width={'100%'}
                        data={data}
                        options={options}
                    />
                </div>
            </div>

            <style type="text/css"> {`
                .history-active-button {
                    color: #1a73e8 !important;
                    font-weight: bold;
                }
                .history-chart-profit {
                    font-size: 13px;
                    font-weight: 600;
                }
                `}
            </style>
        </div>
    );

};